import React from "react";
import { Container, Row, Col, Card } from "reactstrap";
import {
    TrendingUp,
    Network,
    Sparkles,
    ShieldCheck,
    ArrowRight,
} from "lucide-react";

const SHIFTS = [
    {
        icon: TrendingUp,
        from: "Reactive",
        to: "Predictive",
        color: "#6bbac2",
    },
    {
        icon: Network,
        from: "Fragmented",
        to: "Integrated",
        color: "#bfbe28",
    },
    {
        icon: Sparkles,
        from: "Manual",
        to: "AI-Augmented",
        color: "#6bbac2",
    },
    {
        icon: ShieldCheck,
        from: "Short-Term Fixes",
        to: "Long-Term Resilience",
        color: "#bfbe28",
    },
];

function ShiftCard({ item, index, size }) {
    const Icon = item.icon;
    const [hovered, setHovered] = React.useState(false);

    return (
        <Col key={index} xs={12} sm={3} className="mb-3">
            <Card
                className="shift-card h-100"
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                style={{
                    background: hovered
                        ? `linear-gradient(135deg, ${item.color}18, ${item.color}08)`
                        : "rgba(255, 255, 255, 0.04)",
                    backdropFilter: "blur(10px)",
                    border: `1px solid ${hovered ? item.color + "50" : "rgba(255, 255, 255, 0.08)"}`,
                    borderRadius: "16px",
                    padding: size ? "1.5rem 1.5rem" : "1.25rem 1.25rem",
                    transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer",
                    boxShadow: hovered ? `0 12px 32px ${item.color}25` : "none",
                    transform: hovered ? "translateY(-4px)" : "translateY(0)",
                }}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "1.25rem",
                    }}
                >
                    {/* Icon on top */}
                    <div
                        style={{
                            width: size ? 60 : 54,
                            height: size ? 60 : 54,
                            borderRadius: "16px",
                            background: `linear-gradient(145deg, ${item.color}28, ${item.color}12)`,
                            border: `2px solid ${item.color}50`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
                            transform: hovered ? "scale(1.1) rotate(-3deg)" : "scale(1) rotate(0deg)",
                            boxShadow: hovered ? `0 10px 24px ${item.color}35` : "none",
                        }}
                    >
                        <Icon
                            size={size ? 30 : 26}
                            strokeWidth={2.2}
                            style={{ color: item.color }}
                        />
                    </div>
                    {/* Comparison on bottom */}
                    <div style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem", flexWrap: "wrap" }}>
                        {/* Old / Before */}
                        <div
                            style={{
                                flex: "1 1 auto",
                                minWidth: "100px",
                                padding: "0.5rem 0.75rem",
                                borderRadius: "10px",
                                background: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.1)",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.5)",
                                    marginBottom: "0.2rem",
                                }}
                            >
                                Before
                            </div>
                            <span
                                style={{
                                    fontSize: size ? "1.05rem" : "0.98rem",
                                    color: "rgba(255,255,255,0.65)",
                                    fontWeight: 600,
                                    textDecoration: "line-through",
                                }}
                            >
                                {item.from}
                            </span>
                        </div>
                        <ArrowRight
                            size={size ? 22 : 20}
                            strokeWidth={2.5}
                            style={{
                                color: item.color,
                                flexShrink: 0,
                            }}
                        />
                        {/* New / After */}
                        <div
                            style={{
                                flex: "1 1 auto",
                                minWidth: "100px",
                                padding: "0.5rem 0.75rem",
                                borderRadius: "10px",
                                background: `${item.color}18`,
                                border: `1px solid ${item.color}50`,
                                boxShadow: hovered ? `inset 0 0 20px ${item.color}15` : "none",
                                transition: "box-shadow 0.3s ease",
                            }}
                        >
                            <div
                                style={{
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    letterSpacing: "0.12em",
                                    textTransform: "uppercase",
                                    color: item.color,
                                    marginBottom: "0.2rem",
                                }}
                            >
                                After
                            </div>
                            <span
                                style={{
                                    fontSize: size ? "1.15rem" : "1.05rem",
                                    color: item.color,
                                    fontWeight: 700,
                                    letterSpacing: "0.02em",
                                    textShadow: hovered ? `0 0 24px ${item.color}50` : "none",
                                    transition: "text-shadow 0.3s ease",
                                }}
                            >
                                {item.to}
                            </span>
                        </div>
                    </div>
                </div>
            </Card>
        </Col>
    );
}

function WhyItMattersSection() {
    const size =
        document.body.clientWidth >= 928 || document.documentElement.clientWidth >= 928;

    return (
        <section
            className="section py-5"
            style={{
                background: "transparent",
                position: "relative",
                zIndex: 1,
            }}
        >
            <Container>
                <Row className="">
                    <Col lg={6} className="mb-4 mb-lg-0">
                        <h2
                            className="text-white mb-4"
                            style={{
                                fontSize: size ? "2.25rem" : "1.75rem",
                                fontWeight: 700,
                                letterSpacing: "0.04em",
                                lineHeight: "1.2",
                                textTransform: "uppercase",
                                color: "#6bbac2",
                            }}
                        >
                            Why it matters now
                        </h2>
                        <p
                            className="text-white mb-4"
                            style={{
                                fontSize: size ? "1.2rem" : "1.05rem",
                                lineHeight: "1.8",
                                opacity: 0.95,
                            }}
                        >
                            New Zealand's fibre network and digital infrastructure are unlocking a
                            new generation of "weightless" digital exports and AI-enabled services.
                            These depend on high-quality, real-time data about the physical world —
                            exactly what digital twins provide.
                        </p>
                        <p
                            className="text-white mb-3"
                            style={{
                                fontSize: size ? "1.1rem" : "1rem",
                                lineHeight: "1.75",
                                fontWeight: 600,
                                color: "#bfbe28",
                            }}
                        >
                            The result? A shift from:
                        </p>
                    </Col>
                </Row><Row>
                    <Col lg={12}>
                        <Row className="justify-content-center">
                            {SHIFTS.map((item, i) => (
                                <ShiftCard key={i} item={item} index={i} size={size} />
                            ))}
                        </Row>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

export default WhyItMattersSection;
